import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

const pillBase =
  'inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0 text-[10px] font-medium leading-5';

/** Category / special category — white pill, light grey border */
export const categoryPillClassName = cn(
  pillBase,
  'border-gray-200 bg-white text-gray-900'
);

/**
 * Role colors from reference:
 * - Primary Owner / Manager → solid mustard
 * - Owner → soft purple
 * - Staff → mint green
 * - Member / Receptionist / Employee → cool grey-blue
 */
export function getRolePillClassName(role: string): string {
  const normalized = role.trim().toLowerCase();

  if (normalized === 'primary owner' || normalized === 'manager') {
    return cn(pillBase, 'border-transparent bg-amber-400 text-gray-900');
  }
  if (normalized === 'owner') {
    return cn(pillBase, 'border-violet-300 bg-violet-50 text-violet-800');
  }
  if (normalized === 'staff') {
    return cn(pillBase, 'border-emerald-300 bg-emerald-50 text-emerald-800');
  }
  return cn(pillBase, 'border-slate-300 bg-slate-50 text-slate-700');
}

export function CategoryPill({ children }: { children: ReactNode }) {
  return <span className={categoryPillClassName}>{children}</span>;
}

export function RolePill({ role }: { role: string }) {
  return <span className={getRolePillClassName(role)}>{role}</span>;
}

/** Category-style pills, or a muted dash when empty */
export function renderCategoryPills(items: string | string[] | null | undefined) {
  if (!items) {
    return <span className="text-muted-foreground">-</span>;
  }

  const itemArray = Array.isArray(items) ? items : [items];
  if (itemArray.length === 0) {
    return <span className="text-muted-foreground">-</span>;
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {itemArray.map((item, index) => (
        <CategoryPill key={`${item}-${index}`}>{item}</CategoryPill>
      ))}
    </div>
  );
}

/** Role-colored pills, or a muted dash when empty */
export function renderRolePills(roles: string[] | null | undefined) {
  if (!roles || roles.length === 0) {
    return <span className="text-muted-foreground">-</span>;
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {roles.map((role, index) => (
        <RolePill key={`${role}-${index}`} role={role} />
      ))}
    </div>
  );
}
